import React, { useState, useEffect } from 'react';
import styles from './AddTokenForm.module.css';

interface AddTokenFormProps {
  onSubmit: (tokenData: TokenFormData) => void;
  onCancel: () => void;
  initialData?: Partial<TokenFormData>;
}

export interface TokenFormData {
  tokenName: string;
  symbol: string;
  tokenAddress: string;
  openingBalance: number;
  openingDate: string;
  status: 'active' | 'inactive';
  currentBal: number;
  notes: string;
}

const AddTokenForm: React.FC<AddTokenFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState<TokenFormData>({
    tokenName: '',
    symbol: '',
    tokenAddress: '',
    openingBalance: 0,
    openingDate: new Date().toISOString().split('T')[0],
    currentBal: 0,
    status: 'active',
    notes: '',
  });

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'openingBalance' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Token Name and Symbol Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="tokenName" className={styles.label}>
            Token Name <span className={styles.required}>*</span>
          </label>
          <input
            id="tokenName"
            type="text"
            name="tokenName"
            value={formData.tokenName}
            onChange={handleChange}
            className={styles.input}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="symbol" className={styles.label}>
            Symbol <span className={styles.required}>*</span>
          </label>
          <input
            id="symbol"
            type="text"
            name="symbol"
            value={formData.symbol}
            onChange={handleChange}
            className={styles.input}
            required
          />
        </div>
      </div>

      {/* Token Address */}
      <div className={styles.formGroup}>
        <label htmlFor="tokenAddress" className={styles.label}>Token Address</label>
        <input
          id="tokenAddress"
          type="text"
          name="tokenAddress"
          value={formData.tokenAddress}
          onChange={handleChange}
          className={styles.input}
        />
      </div>

      {/* Opening Balance and Opening Date Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="openingBalance" className={styles.label}>Opening Balance</label>
          <input
            id="openingBalance"
            type="number"
            name="openingBalance"
            value={formData.openingBalance}
            onChange={handleChange}
            className={styles.input}
            step="0.0001"
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="openingDate" className={styles.label}>Opening Date</label>
          <input
            id="openingDate"
            type="date"
            name="openingDate"
            value={formData.openingDate}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
      </div>

      {/* Status */}
      <div className={styles.formGroup}>
        <label htmlFor="status" className={styles.label}>Status</label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          className={styles.select}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Notes */}
      <div className={styles.formGroup}>
        <label htmlFor="notes" className={styles.label}>Notes</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className={styles.textarea}
          rows={3}
        />
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
        <button type="submit" className={styles.createButton}>
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default AddTokenForm;
